import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash-es';
import { createAsyncAction } from '../action/index';

const login = createAsyncAction('login', 'login');
const logout = createAsyncAction('logout', 'logout');
const updateUser = createAsyncAction('updateUser', 'updateUser');
const verifyOtp = createAsyncAction('verifyOtp', 'verifyOtp');
const checkWhatsAppConsent = createAsyncAction('checkWhatsAppConsent', 'checkWhatsAppConsent');
const whatsAppOptIn = createAsyncAction('whatsAppOptIn', 'whatsAppOptIn');
const whatsAppOptOut = createAsyncAction('whatsAppOptOut', 'whatsAppOptOut');
const createNewPatient = createAsyncAction('createNewPatient', 'createNewPatient');
const getUserDetailsFromToken = createAsyncAction('getUserDetailsFromToken', 'getUserDetailsFromToken');

export enum Status {
  pending = 'pending',
  fulfilled = 'fulfilled',
  rejected = 'rejected',
}

interface InitialState {
  user: {
    dateOfBirth: string;
    userID: string;
    firstName: string;
    lastName: string;
    mobileNumber?: number;
    email: string;
    gender: string;
    address?: {
      houseNo?: string,
      society?: string;
      landmark?: string;
      state?: string;
      area?: string;
      pinCode?: number;
      city?: string;
    }
  },
  chatData: any,
  accessToken: string;
  isLogging: boolean,
  otpVerifying: boolean,
  loginStatus: string,
  logoutStatus: string,
  loginError: string,
  otpStatus: string,
  otpError: string,
  userUpdateStatus: string,
  userUpdateError: any,
  whatsAppConsentStatus: string,
  whatsAppOptType: string,
  patientCreated: boolean,
  patientCreating: boolean;
  newlyCreatedPatient: any,
  createdPatientError: string,
}

const initialState = {
  user: {
    dateOfBirth: '',
    userID: '',
    firstName: '',
    lastName: '',
    email: '',
    gender: '',
  },
  chatData: {},
  accessToken: '',
  isLogging: false,
  otpVerifying: false,
  loginStatus: '',
  logoutStatus: '',
  userUpdateStatus: '',
  userUpdateError: {},
  loginError: '',
  otpStatus: '',
  otpError: '',
  whatsAppConsentStatus: '',
  whatsAppOptType: '',
  createdPatientError: '',
  patientCreated: false,
  patientCreating: false,
  newlyCreatedPatient: {},
} as InitialState;

export const counterSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateAuthState: (state, action) => Object.assign(state, action.payload),
  },
  extraReducers: {
    [login.pending as any]: (state) => {
      state.isLogging = true;
      state.loginStatus = Status.pending;
    },
    [login.fulfilled as any]: (state) => {
      state.isLogging = false;
      state.loginStatus = Status.fulfilled;
    },
    [login.rejected as any]: (state, { payload }) => {
      state.isLogging = false;
      state.loginStatus = Status.rejected;
      state.loginError = get(payload, 'errors.mobileNumber[0]', '');
    },
    [logout.pending as any]: (state) => {
      state.logoutStatus = Status.pending;
    },
    [logout.fulfilled as any]: (state) => {
      state.logoutStatus = Status.fulfilled;
    },
    [logout.rejected as any]: (state) => {
      state.logoutStatus = Status.rejected;
    },
    [verifyOtp.pending as any]: (state) => {
      state.otpVerifying = true;
      state.otpStatus = Status.pending;
    },
    [verifyOtp.fulfilled as any]: (state, { payload }) => {
      state.accessToken = payload.token;
      state.user = payload.data;
      state.otpVerifying = false;
      state.otpStatus = Status.fulfilled;
    },
    [verifyOtp.rejected as any]: (state, { payload }) => {
      state.otpVerifying = false;
      state.otpStatus = Status.rejected;
      state.otpError = get(payload, 'errors.otp[0]', '');
    },
    [updateUser.pending as any]: (state) => {
      state.userUpdateStatus = Status.pending;
      state.userUpdateError = {};
    },
    [updateUser.fulfilled as any]: (state, { payload }) => {
      state.user = payload.user;
      state.userUpdateStatus = Status.fulfilled;
    },
    [updateUser.rejected as any]: (state, { payload }) => {
      state.userUpdateStatus = Status.rejected;
      state.userUpdateError = get(payload, 'errors.messages', {});
    },
    [checkWhatsAppConsent.pending as any]: (state) => {
      state.whatsAppConsentStatus = '';
    },
    [checkWhatsAppConsent.fulfilled as any]: (state, { payload }) => {
      state.whatsAppConsentStatus = payload.consentStatus;
    },
    [whatsAppOptIn.fulfilled as any]: (state) => {
      state.whatsAppOptType = 'opt-in';
    },
    [whatsAppOptOut.fulfilled as any]: (state) => {
      state.whatsAppOptType = 'opt-out';
    },
    [createNewPatient.pending as any]: (state) => {
      state.newlyCreatedPatient = {};
      state.patientCreated = false;
      state.patientCreating = true;
      state.createdPatientError = '';
    },
    [createNewPatient.fulfilled as any]: (state, { payload }) => {
      state.newlyCreatedPatient = payload.patientDetails;
      state.patientCreated = true;
      state.patientCreating = false;
    },
    [createNewPatient.rejected as any]: (state, { payload }) => {
      state.patientCreated = false;
      state.patientCreating = false;
      state.createdPatientError = get(payload, 'messages[0]', '');
    },
    [getUserDetailsFromToken.fulfilled as any]: (state, { payload }) => {
      state.accessToken = payload.token;
      state.user = payload.data;
    },
  },
});

const { updateAuthState } = counterSlice.actions;
export {
  updateAuthState,
  login,
  logout,
  updateUser,
  verifyOtp,
  checkWhatsAppConsent,
  whatsAppOptIn,
  whatsAppOptOut,
  createNewPatient,
  getUserDetailsFromToken,
};
export default counterSlice.reducer;